import { useState, useEffect, useCallback, useRef } from "react";
import useIsMobile from "./useIsMobile";

const ALLOWED_DOMAINS = ["stuff.co.nz", "stuffdigital.co.nz"];
const CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

function decodeJwt(token) {
  const base64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
  return JSON.parse(atob(base64));
}

export default function AuthGate({ children }) {
  const [user, setUser] = useState(null);
  const [error, setError] = useState(null);
  const [ready, setReady] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const mob = useIsMobile();

  const handleCredential = useCallback((response) => {
    try {
      const payload = decodeJwt(response.credential);
      const domain = payload.email.split("@")[1];
      if (!ALLOWED_DOMAINS.includes(domain)) {
        setError("You don't have access to this application.");
        return;
      }
      sessionStorage.setItem("dispatch_user", JSON.stringify({
        name: payload.name,
        email: payload.email,
        picture: payload.picture,
      }));
      setUser({ name: payload.name, email: payload.email, picture: payload.picture });
      setError(null);
    } catch {
      setError("Sign-in failed. Please try again.");
    }
  }, []);

  useEffect(() => {
    const stored = sessionStorage.getItem("dispatch_user");
    if (stored) {
      try { setUser(JSON.parse(stored)); } catch { sessionStorage.removeItem("dispatch_user"); }
    }

    function initGoogle() {
      if (!window.google?.accounts) return false;
      window.google.accounts.id.initialize({
        client_id: CLIENT_ID,
        callback: handleCredential,
        auto_select: true,
      });
      setReady(true);
      return true;
    }

    if (!initGoogle()) {
      const interval = setInterval(() => {
        if (initGoogle()) clearInterval(interval);
      }, 200);
      return () => clearInterval(interval);
    }
  }, [handleCredential]);

  useEffect(() => {
    if (ready && !user && btnRef.current) {
      window.google.accounts.id.renderButton(btnRef.current, {
        type: "standard",
        theme: "filled_black",
        size: "large",
        text: "signin_with",
        shape: "rectangular",
        width: 280,
      });
    }
  }, [ready, user]);

  // Close menu when tapping outside
  useEffect(() => {
    if (!menuOpen) return;
    function handleClick(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("touchstart", handleClick);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("touchstart", handleClick);
    };
  }, [menuOpen]);

  function signOut() {
    sessionStorage.removeItem("dispatch_user");
    setUser(null);
    setMenuOpen(false);
    window.google?.accounts?.id?.disableAutoSelect();
  }

  if (!CLIENT_ID) {
    return (
      <div style={styles.container}>
        <div style={{ ...styles.card, padding: mob ? "24px 20px" : "32px 36px" }}>
          <div style={{ fontSize: 14, color: "#ef4444" }}>
            Missing <code style={{ background: "#1e2d42", padding: "2px 6px", borderRadius: 3 }}>REACT_APP_GOOGLE_CLIENT_ID</code> environment variable.
          </div>
          <div style={{ fontSize: 12, color: "#64748b", marginTop: 8 }}>
            See .env.example for setup instructions.
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div style={styles.container}>
        <div style={{ ...styles.card, padding: mob ? "24px 20px" : "32px 36px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 20 }}>
            <div style={{ width: 24, height: 24, background: "#3b82f6", borderRadius: 4, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <span style={{ fontSize: 13, fontWeight: 900, color: "#fff" }}>S</span>
            </div>
            <span style={{ fontSize: 14, fontWeight: 700, color: "#e2e8f0" }}>AI Production Engine</span>
          </div>
          <div style={{ fontSize: 12, color: "#64748b", marginBottom: 20 }}>
            Sign in with your Google account to continue.
          </div>
          <div ref={btnRef} style={{ minHeight: 44 }} />
          {error && (
            <div style={{ fontSize: 12, color: "#ef4444", marginTop: 14, padding: "8px 12px", background: "#1c1017", borderRadius: 6, border: "1px solid #ef444440" }}>
              {error}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div ref={menuRef} style={{ position: "fixed", top: mob ? 4 : 8, right: mob ? 6 : 16, zIndex: 200 }}>
        <button
          onClick={() => setMenuOpen(!menuOpen)}
          style={{
            width: mob ? 36 : 32, height: mob ? 36 : 32, borderRadius: "50%", padding: 0,
            border: menuOpen ? "2px solid #3b82f6" : "2px solid transparent",
            cursor: "pointer", background: "transparent", overflow: "hidden",
          }}
        >
          <img src={user.picture} alt="" style={{ width: mob ? 32 : 28, height: mob ? 32 : 28, borderRadius: "50%" }} referrerPolicy="no-referrer" />
        </button>
        {menuOpen && (
          <div style={{
            position: "absolute", top: mob ? 42 : 38, right: 0,
            background: "#0f1623", border: "1px solid #1e2d42",
            borderRadius: 8, padding: "12px 14px", minWidth: 180,
            boxShadow: "0 8px 24px #00000060",
          }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: "#e2e8f0", marginBottom: 2 }}>{user.name}</div>
            <div style={{ fontSize: 11, color: "#475569", marginBottom: 12 }}>{user.email}</div>
            <button onClick={signOut} style={{
              fontSize: 11, color: "#93c5fd", background: "#1e3a5f",
              border: "none", borderRadius: 5, padding: "6px 12px",
              cursor: "pointer", fontWeight: 600, width: "100%",
            }}>Sign out</button>
          </div>
        )}
      </div>
      {children}
    </div>
  );
}

const styles = {
  container: {
    minHeight: "100vh",
    background: "#070d1a",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: "'IBM Plex Sans', sans-serif",
  },
  card: {
    background: "#0f1623",
    border: "1px solid #1e2d42",
    borderRadius: 12,
    padding: "32px 36px",
    maxWidth: 360,
    width: "100%",
    margin: "0 16px",
  },
};
